/**
 * CrmAuditPanel — recent CRM operator actions surfaced inside the SuperAdmin
 * -> Operations -> CRM subtab.
 *
 * Renders a compact list (not a full table) of the most recent audit rows filtered
 * to `action LIKE 'crm.%'`. Each row shows a relative timestamp, the operator's
 * display name, and a human-readable action label. Clicking a row toggles a
 * JSON pretty-print of the `detail` field (the tally / previous_state /
 * new_state payload emitted by admin_crm handlers).
 *
 * The component is presentational: the parent owns the fetch and passes rows
 * in the desired order (server sorts DESC by occurred_at). The component
 * preserves that ordering and caps the visible list at 50 rows.
 *
 * @param {Object}   props
 * @param {Array<{timestamp:string, display_name:?string, action:string, detail:?Object}>} props.auditRows
 *        Audit rows already sorted newest-first by the parent. `display_name`
 *        is the operator's name as returned by the backend's `GET /crm/audit`
 *        endpoint (see `routes/admin_crm.py::list_audit`); falls back to
 *        `System` in the UI when null.
 * @returns {JSX.Element}
 */
import React, { useState, useCallback } from 'react';

/** Hard cap on visible rows — matches the spec for the in-tab compact panel. */
const MAX_ROWS = 50;

const MS_PER_MIN = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Human-readable label lookup for the CRM audit action codes defined in
 * `utils/audit.py::AUDIT_ACTIONS`. Kept close to the component so tests can
 * lock the mapping and reviewers can eyeball new additions.
 */
const CRM_ACTION_LABELS = {
  'crm.backfill': 'Backfill',
  'crm.reconcile': 'Reconcile',
  'crm.cleanup_orphans': 'Cleanup orphans',
  'crm.resync_manual': 'Manual resync',
  'crm.health_probe': 'Health probe',
  'crm.link_feedback': 'Link feedback',
};

/**
 * Translate an AUDIT_ACTIONS value to the operator-facing label used in the
 * panel. Falls back to a lightly humanized version of the raw code for
 * unknown values so the panel keeps rendering when new actions land ahead of
 * a frontend update.
 *
 * @param {string} action - e.g. 'crm.backfill' or 'crm.resync_manual'
 * @returns {string} Display label, e.g. 'Backfill' / 'Manual resync'
 */
export function labelForCrmAction(action) {
  if (!action) return '—';
  if (CRM_ACTION_LABELS[action]) return CRM_ACTION_LABELS[action];
  // Fallback: strip the 'crm.' prefix and title-case the verb.
  const verb = String(action).replace(/^crm\./, '').replace(/_/g, ' ');
  if (!verb) return action;
  return verb.charAt(0).toUpperCase() + verb.slice(1);
}

/**
 * Format an ISO timestamp as a compact relative time string.
 * @param {?string} iso - ISO 8601 timestamp, or null/undefined.
 * @returns {string} e.g. "just now", "3m ago", "2h ago", "yesterday", "3d ago", or a date.
 */
function formatRelativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const t = then.getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  if (diff < MS_PER_MIN) return 'just now';
  if (diff < MS_PER_HOUR) {
    const m = Math.floor(diff / MS_PER_MIN);
    return `${m}m ago`;
  }
  if (diff < MS_PER_DAY) {
    const h = Math.floor(diff / MS_PER_HOUR);
    return `${h}h ago`;
  }
  const days = Math.floor(diff / MS_PER_DAY);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

/**
 * Pretty-print a detail payload for the expand region. Object / array values
 * get JSON.stringify with 2-space indent; primitives are rendered as-is.
 * @param {*} detail
 * @returns {string}
 */
function formatDetail(detail) {
  if (detail === null || detail === undefined) return '(no detail)';
  if (typeof detail === 'string') return detail;
  try {
    return JSON.stringify(detail, null, 2);
  } catch (_e) {
    return String(detail);
  }
}

function CrmAuditPanel({ auditRows }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  /**
   * Toggle the expanded-detail region for a given row index.
   * @param {number} index
   */
  const toggleExpanded = useCallback((index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  const rows = Array.isArray(auditRows) ? auditRows.slice(0, MAX_ROWS) : [];

  if (rows.length === 0) {
    return (
      <div
        className="empty-state crm-audit-panel__empty"
        role="status"
        data-testid="crm-audit-panel-empty"
      >
        <h3 className="empty-state__title">No recent CRM operations recorded.</h3>
        <p className="empty-state__description">
          Operator actions (backfill, reconcile, manual resync, and health probes)
          will appear here as they run.
        </p>
      </div>
    );
  }

  return (
    <section
      className="crm-audit-panel"
      aria-label="Recent CRM operator actions"
      data-testid="crm-audit-panel"
    >
      <ol className="crm-audit-panel__list">
        {rows.map((row, i) => {
          const isExpanded = expandedIndex === i;
          // Backend contract: /admin/crm/audit returns `display_name`, not
          // `user_display_name` (see routes/admin_crm.py::list_audit line 702).
          const operator = (row && row.display_name) || 'System';
          const action = (row && row.action) || '';
          const label = labelForCrmAction(action);
          const rel = formatRelativeTime(row && row.timestamp);
          const detailId = `crm-audit-detail-${i}`;
          return (
            <li
              key={i}
              className={
                isExpanded
                  ? 'crm-audit-panel__row crm-audit-panel__row--expanded'
                  : 'crm-audit-panel__row'
              }
              data-testid={`crm-audit-row-${i}`}
            >
              <button
                type="button"
                className="crm-audit-panel__row-btn"
                onClick={() => toggleExpanded(i)}
                aria-expanded={isExpanded}
                aria-controls={detailId}
                aria-label={`${label} by ${operator} ${rel} — toggle detail`}
                data-testid={`crm-audit-row-toggle-${i}`}
              >
                <span
                  className="crm-audit-panel__time crm-audit-panel__mono"
                  title={(row && row.timestamp) || ''}
                >
                  {rel}
                </span>
                <span className="crm-audit-panel__operator">{operator}</span>
                <span
                  className="crm-audit-panel__action"
                  data-testid={`crm-audit-row-action-${i}`}
                >
                  {label}
                </span>
                <span
                  className="crm-audit-panel__chevron"
                  aria-hidden="true"
                >
                  {isExpanded ? '▾' : '▸'}
                </span>
              </button>
              {isExpanded ? (
                <pre
                  id={detailId}
                  className="crm-audit-panel__detail crm-audit-panel__mono"
                  data-testid={`crm-audit-row-detail-${i}`}
                >
                  {formatDetail(row && row.detail)}
                </pre>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default CrmAuditPanel;
